import React, { useEffect, useState } from 'react';
import api from '../../../api';
import Step1 from './Steps/Step1';
import Step2 from './Steps/Step2';
import Step3 from './Steps/Step3';
import { get } from 'lodash';

function Orders({ trailId, minDate }) {
    // ui
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [timeBufferError, setTimeBufferError] = useState('');

    // trail data
    const [trail, setTrail] = useState({});

    // available equipments data
    const [slots, setSlots] = useState([]);

    // pricing
    const [pricing, setPricing] = useState({});

    // order data
    const [date, setDate] = useState(minDate); // "2022-02-11" <- date format is YYYY-MM-DD
    const [slot, setSlot] = useState({});
    const [hour, setHour] = useState('');
    const [price, setPrice] = useState(0);
    const [equipments, setEquipments] = useState([]); // [{ "id": 1, "equipmentToTrailId": 1, "count": 4 }]
    const [recipient, setRecipient] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
    });
    const [paymentType, setPaymentType] = useState(1);

    // get init data
    useEffect(() => {
        const getTrail = async () => {
            try {
                const data = await api.findTrail(trailId);
                setTrail(data);
            } catch (error) {
                alert(
                    'Coś poszło nie tak. Odśwież stronę lub spróbuj ponownie później.',
                );
            }
        };
        getTrail();
    }, []);

    const fetchSlotsData = async () => {
        let data = await api.findAvailableEquipments(trailId, date, equipments);
        data = data.filter((slot) => slot.startTime > new Date().toISOString());
        setSlots(data);
    };

    useEffect(() => {
        const init = async () => {
            try {
                await fetchSlotsData();
            } catch (error) {
                alert(
                    'Coś poszło nie tak. Odśwież stronę lub spróbuj ponownie później.',
                );
            }
        };
        init();
    }, [date, equipments]);

    useEffect(() => {
        setSlot({});
        setHour('');
    }, [date]);

    useEffect(() => {
        if (!slot.startTime) return;

        const checkTimeBuffers = async () => {
            setTimeBufferError('');
            try {
                await api.checkTimeBuffers(trailId, slot.startTime);
            } catch (error) {
                const msg =
                    error.response.data.message ||
                    'Rezerwacje w tym czasie nie jest możliwa. Wybierz inną godzinę lub dzień.';
                setTimeBufferError(msg);
            }
        };
        checkTimeBuffers();
    }, [slot]);

    // calculate price
    useEffect(() => {
        const getPrice = async () => {
            // aktualizuj cene przy zmiane dania, ilosci, godziny
            const selectedDate = slot.startTime || new Date(date);
            const pricing = await api.getPrices(
                trailId,
                selectedDate,
                equipments,
            );
            setPricing(pricing);
            setPrice(pricing.newTotalPrice || pricing.totalPrice);
        };
        getPrice();
    }, [equipments, slot, date]);

    const onHourChange = (hour) => {
        setHour(hour);
        setSlot(hour ? slots.find((x) => x.startTimeHuman === hour) : {});
    };

    const onSubmit = async () => {
        const data = {
            trailId,
            price,
            equipments,
            userFirstName: recipient.firstName,
            userLastName: recipient.lastName,
            userEmail: recipient.email,
            userPhone: recipient.phone,
            date: slot.startTime,
            paymentChannelId: paymentType,
        };

        setLoading(true);

        try {
            // confirm availability
            const isStillAvailable = await api.confirmAvailableEquipments(
                trailId,
                slot.startTime,
                equipments,
            );
            if (!isStillAvailable) {
                setLoading(false);
                return;
            }

            //
            const order = await api.makeOrder(data);
            window.location.href = `/rezerwuj/${order.id}`;
        } catch (error) {
            console.log('Error:', error.response.error);
            if (error.response.data?.errors) {
                setErrors(Object.values(error.response.data?.errors || []));
            } else if (error.response.data?.message) {
                setErrors([error.response.data?.message]);
            } else {
                alert(
                    'Coś poszło nie tak. Spróbuj odświeżyć stronę. Jeśli problem wciąż występuje dokonaj rezerwacji telefonicznie',
                );
            }
            setLoading(false);
            fetchSlotsData();
        }
    };

    return (
        <div className="row">
            {/* summary  */}
            <div className="col-lg-4 col-md-6 col-12 d-lg-block d-none">
                <div
                    className="sidebar sticky-bar p-4 rounded shadow"
                    style={{ fontSize: '105%', background: 'white', zIndex: 2 }}
                >
                    <div className="widget">
                        <h5 className="widget-title">Podsumowanie:</h5>
                        <p className="mb-1">
                            <span className="text-muted">Organizator:</span>{' '}
                            {trail.company?.title}{' '}
                        </p>
                        <p className="mb-1">
                            <span className="text-muted">Trasa:</span>{' '}
                            {trail.title}
                        </p>
                        <p className="mb-1">
                            <span className="text-muted">Data:</span> {date}{' '}
                            <span className="hide-sm"></span>
                        </p>
                        <p className="mb-1">
                            <span className="text-muted">Godzina:</span> {hour}
                        </p>
                        <p className="mb-1">
                            <span className="text-muted">Cena:</span>{' '}
                            <span>{price}</span>zł
                        </p>
                    </div>
                </div>

                {trail.description && (
                    <div className="sidebar sticky-bar p-4 rounded shadow mt-3" style={{ background: 'white' }}>
                        <div className="widget">
                            <h5 className="widget-title">Opis:</h5>
                            <p className="mb-1">{trail.description} </p>
                        </div>
                    </div>
                )}
            </div>

            <div className="col-lg-8 col-12 p-4 shadow rounded" style={{ background: 'white' }}>
                {step === 0 && (
                    <>
                        <Step1
                            minDate={minDate}
                            date={date}
                            onDateChange={setDate}
                            hour={hour}
                            onHourChange={onHourChange}
                            equipmentsToTrail={trail.equipmentToTrail}
                            slots={slots}
                            equipments={equipments}
                            onEquipmentsChange={setEquipments}
                            pricing={pricing}
                        />
                        <button
                            className="btn btn-primary"
                            disabled={
                                !equipments.length || !hour || timeBufferError
                            }
                            onClick={() => setStep(1)}
                        >
                            Dalej
                        </button>
                    </>
                )}

                {step === 1 && (
                    <>
                        <Step2
                            recipient={recipient}
                            onRecipientChange={setRecipient}
                        />
                        <div className="buttons-group">
                            <button
                                className="btn btn-secondary"
                                style={{ marginRight: 10 }}
                                onClick={() => setStep(0)}
                            >
                                Wróć
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => setStep(2)}
                                disabled={
                                    !recipient.firstName ||
                                    !recipient.lastName ||
                                    !recipient.phone ||
                                    !recipient.email
                                }
                            >
                                Dalej
                            </button>
                        </div>
                    </>
                )}
                {step === 2 && (
                    <>
                        <Step3
                            trail={trail}
                            date={date}
                            hour={hour}
                            recipient={recipient}
                            paymentType={paymentType}
                            onPaymentTypeChange={setPaymentType}
                            equipments={equipments}
                            price={price}
                        />

                        <div className="buttons-group pt-3 loadingOrder">
                            <button
                                className="btn btn-secondary"
                                style={{ marginRight: 10 }}
                                onClick={() => setStep(1)}
                                disabled={loading}
                            >
                                Wróć
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => onSubmit()}
                                disabled={!paymentType || loading}
                            >
                                {loading && (
                                    <span
                                        className="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                        style={{ marginRight: 10 }}
                                    ></span>
                                )}
                                Rezerwuj
                            </button>
                        </div>
                    </>
                )}

                {errors.length ? (
                    <div className="alert alert-danger mt-3">
                        <ul className="mb-0">
                            {errors.map((error) => (
                                <li>{error}</li>
                            ))}
                        </ul>
                    </div>
                ) : null}

                {timeBufferError && (
                    <div className="alert alert-danger mt-3">
                        <ul className="mb-0">
                            <li>{timeBufferError}</li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Orders;
